import React, { Suspense, useState } from 'react';
import ReactModal from 'react-modal';
import PeopleModal from './people-modal/PeopleModal';
import { Await, Link, useLoaderData } from 'react-router-dom';
import Spinner from '../share/Spinner';
import { ActivityTemplate, TemplateStatus } from '../models';
import { nanoid } from 'nanoid';
import { ActionButton } from './ActionButton';
import { useTranslation } from 'react-i18next';

function StatusBadge({ status }: { status: string }) {
  const [t] = useTranslation(['admin']);
  let statusClasses;
  switch (status) {
    case TemplateStatus.PUBLISHED:
      statusClasses = 'bg-green-100 text-green-800 border-green-400';
      break;
    case TemplateStatus.ARCHIVED:
      statusClasses = 'bg-red-100 text-red-800 border-red-400';
      break;
    case TemplateStatus.EDITING:
      statusClasses = 'bg-gray-100 text-gray-800 border-gray-500';
      break;
    default:
      statusClasses = 'bg-blue-100 text-blue-800 border-blue-400';
  }

  return (
    <span
      className={`text-xs font-medium mr-2 px-2.5 py-0.5 rounded border ${statusClasses}`}
    >
      {t(status)}
    </span>
  );
}

function TemplateItem({ template }: { template: ActivityTemplate }) {
  const [t] = useTranslation(['admin']);
  return (
    <div className="drop-shadow w-full">
      <div className="w-full h-[104px] bg-white rounded p-5 flex">
        <div className="flex justify-center items-center mr-5">
          <img
            alt=""
            className="h-14 aspect-square rounded"
            src={template.imageUrl}
          />
        </div>
        <div className="flex flex-col justify-center h-full flex-grow w-2/3 mr-5">
          <div className="flex items-center gap-2">
            <span className="text-xl font-medium text-group-primary">
              {template.name}
            </span>
            <span className="text-sm font-medium text-gray-500">
              {template.status === TemplateStatus.PUBLISHED &&
                `[${template.id}]`}
            </span>
            <StatusBadge status={template.status} />
          </div>
          <span className="text-sm line-clamp-1">{template.description}</span>
        </div>
        <div className="flex flex-col items-end justify-around w-[140px]">
          <ActionButton template={template} />
          <div className="w-full flex justify-center self-center rounded-3xl text-white bg-group-primary text-xs font-medium py-1">
            <span>
              {t('duration_count', '活動天數： {{count}} 天', {
                count: Number(template.duration),
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TemplateList() {
  const [t] = useTranslation(['admin']);
  const deferredData = useLoaderData() as { templates: ActivityTemplate[] };
  const [isPeopleModalOpen, setIsPeopleModalOpen] = useState(false);

  const peopleModalToggle = () => {
    setIsPeopleModalOpen((prevState) => !prevState);
  };

  const wrapFunc = (templates: ActivityTemplate[]) => {
    return (
      <div className="h-admin w-full flex flex-col max-h-full">
        <div className="flex w-full justify-between items-center mt-12 mb-6 px-1">
          <span className="text-4xl text-group-primary font-light">
            {t('template_list', '活動範本列表')}
          </span>
          <button
            className="flex justify-center items-center text-group-primary text-sm border border-group-primary py-3 px-8 rounded"
            onClick={peopleModalToggle}
          >
            <span className="material-symbols-outlined">manage_accounts</span>
            {t('manage_account', '人員管理')}
          </button>
        </div>
        <div className="w-full flex-grow-1 overflow-auto p-1 space-y-6">
          {templates
            .filter((template) => template.status !== TemplateStatus.ARCHIVED)
            .map((template) => {
              return <TemplateItem key={nanoid()} template={template} />;
            })}
        </div>
        <div className="flex w-full justify-center mt-6">
          <Link to="new">
            <button className="bg-group-primary text-white p-3 w-full xl:w-80 rounded text-center disabled:opacity-30 disabled:cursor-not-allowed mb-6">
              {t('add_template', '新增活動範本')}
            </button>
          </Link>
        </div>
      </div>
    );
  };

  return (
    <>
      <Suspense fallback={<Spinner />}>
        <Await resolve={deferredData.templates}>{wrapFunc}</Await>
      </Suspense>
      <ReactModal
        isOpen={isPeopleModalOpen}
        overlayClassName="z-[51] fixed bg-black/[.4] inset-0 backdrop-blur-sm"
        className="flex h-full w-full justify-center items-center px-5"
      >
        <PeopleModal onToggle={peopleModalToggle} />
      </ReactModal>
    </>
  );
}
